import { inject, Signal } from '@angular/core';

import {
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { CompanyCategory } from 'src/app/core/models/core.models';
import { CompanyApiService } from '../services/company-api.service';

/**
 * Content-category tree for the given root category ids
 * (GET /company/user_category_tree).
 *
 * The consumer edits the returned tree locally (drag & drop) before saving it back,
 * so only the mount refetch is kept: a background refetch landing mid-edit would
 * rebuild the node list and drop pending changes.
 */
export function injectCategoryTreeQuery(categoryIds: Signal<number[]>) {
  const companyApi = inject(CompanyApiService);

  return injectQuery(() => ({
    queryKey: ['company', 'user_category_tree', categoryIds()],
    queryFn: () => firstValueFrom(companyApi.getCategoriesTree(categoryIds())),
    enabled: categoryIds().length > 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  }));
}

/**
 * Create a content category (POST /company/category). Builds the
 * `{ company_category: { id: null, name } }` body the backend expects.
 */
export function injectCreateCategoryMutation() {
  const companyApi = inject(CompanyApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (categoryName: string) =>
      firstValueFrom(
        companyApi.createCategory({
          company_category: { id: null, name: categoryName },
        }),
      ),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant(
          'admin-module.msg.content-category-created-successfully',
        ),
      ),
    onError: () =>
      toast.showError(
        translate.instant('admin-module.msg.error-creating-content-category'),
      ),
  }));
}

/** Update a content-category tree (POST /company/categories). */
export function injectUpdateCategoryTreeMutation() {
  const companyApi = inject(CompanyApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: (companyCategory: CompanyCategory) =>
      firstValueFrom(companyApi.updateCategoryTree(companyCategory)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant(
          'admin-module.msg.content-category-updated-successfully',
        ),
      ),
    onError: () =>
      toast.showError(
        translate.instant('admin-module.msg.error-updating-content-category'),
      ),
  }));
}
